import { useState, useEffect, useCallback } from 'react';
import { reservationAPI, extractApiError } from '../services/api';
import { formatDate, parseISOString } from '../utils/timeUtils';

/**
 * 予約データを管理するカスタムフック
 * @param {Date} startDate - 開始日
 * @param {Date} endDate - 終了日
 * @param {Object} options - オプション
 * @param {boolean} options.enabled - フェッチを有効にするかどうか（デフォルト: true）
 */
export const useReservations = (startDate, endDate, { enabled = true } = {}) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // APIレスポンスを内部表現に正規化
  const normalizeReservation = (r) => {
    const id = r.reservation_id || r.id;
    const startISO = r.start_datetime || r.start;
    const endISO = r.end_datetime || r.end;
    return {
      ...r,
      id,
      reservation_id: id,
      start_datetime: startISO,
      end_datetime: endISO,
      start: parseISOString(startISO),
      end: parseISOString(endISO),
    };
  };

  const upsertReservation = (list, r) => {
    const id = r.reservation_id || r.id;
    let replaced = false;
    const next = list.map((item) => {
      if ((item.id || item.reservation_id) === id) {
        replaced = true;
        return normalizeReservation(r);
      }
      return item;
    });
    if (!replaced) next.push(normalizeReservation(r));
    return next;
  };

  /**
   * 予約一覧を取得（手動呼び出し用）
   */
  const fetchReservations = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      // 週の開始日と終了日の年月を取得
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth() + 1;
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;

      // 週の表示範囲に含まれるすべての月のデータを取得
      let allData = [];
      const monthsToFetch = new Set();

      // 週の各日付の月を収集
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        monthsToFetch.add(`${year}-${month}`);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // 各月のデータを取得
      for (const monthKey of monthsToFetch) {
        const [year, month] = monthKey.split('-').map(Number);
        const monthData = await reservationAPI.getCalendarReservations(year, month);
        allData = [...allData, ...monthData];
      }

      // 重複する予約を除去（同じreservation_idの予約は1つだけ残す）
      const uniqueReservations = [];
      const seenIds = new Set();
      for (const reservation of allData) {
        const id = reservation.id || reservation.reservation_id;
        if (!seenIds.has(id)) {
          seenIds.add(id);
          uniqueReservations.push(reservation);
        }
      }
      allData = uniqueReservations;

      // APIレスポンスのフィールド名を統一
      const parsedData = allData.map(normalizeReservation);

      setReservations(parsedData);
    } catch (err) {
      console.error('Failed to fetch reservations:', err);
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * 予約を作成
   */
  const createReservation = async (reservationData) => {
    try {
      console.log('useReservations - Creating reservation with data:', reservationData);
      const newReservation = await reservationAPI.createReservation(reservationData);

      // 予約リストに差分追加（重複防止）
      setReservations(prev => upsertReservation(prev, newReservation));

      return { success: true, data: newReservation };
    } catch (err) {
      console.error('Failed to create reservation:', err);
      return {
        success: false,
        error: extractApiError(err),
      };
    }
  };

  /**
   * 予約を更新
   * @param {number} reservationId - 予約ID
   * @param {object} updateData - 更新データ（update_scopeを含む場合あり）
   */
  const updateReservation = async (reservationId, updateData) => {
    try {
      const updatedReservation = await reservationAPI.updateReservation(reservationId, updateData);

      // 企業変更などサーバー側で加工される項目（表示名等）も含め反映させるため常に再取得
      await fetchReservations();

      return { success: true, data: updatedReservation };
    } catch (err) {
      console.error('Failed to update reservation:', err);
      return {
        success: false,
        error: extractApiError(err),
      };
    }
  };

  /**
   * 予約を削除
   * @param {number} reservationId - 予約ID
   * @param {string} scope - 繰り返し予約の削除範囲 ('this', 'future', 'all')
   */
  const deleteReservation = async (reservationId, scope = null) => {
    try {
      await reservationAPI.deleteReservation(reservationId, scope);

      // scopeが指定されている場合は、全体をリフレッシュ
      if (scope && (scope === 'future' || scope === 'all')) {
        await fetchReservations();
      } else {
        // 予約リストから削除
        setReservations(prev => prev.filter(res => res.id !== reservationId));
      }

      return { success: true };
    } catch (err) {
      console.error('Failed to delete reservation:', err);
      return {
        success: false,
        error: extractApiError(err),
      };
    }
  };

  /**
   * 競合チェック
   */
  const checkConflict = async (conflictData) => {
    try {
      const result = await reservationAPI.checkConflict(conflictData);
      return result;
    } catch (err) {
      console.error('Failed to check conflict:', err);
      return {
        has_conflict: false,
        conflicting_reservations: [],
        error: extractApiError(err),
      };
    }
  };

  /**
   * 指定日の予約を取得
   */
  const getReservationsForDate = useCallback(
    (date) => {
      const dateStr = formatDate(date);
      return reservations.filter(reservation => {
        const resDateStr = formatDate(reservation.start);
        return resDateStr === dateStr;
      });
    },
    [reservations]
  );

  /**
   * 指定会議室・日付の予約を取得
   */
  const getReservationsForRoomAndDate = useCallback(
    (roomType, date) => {
      const dateReservations = getReservationsForDate(date);
      return dateReservations.filter(reservation => reservation.room_type === roomType);
    },
    [getReservationsForDate]
  );

  // 日付が変更されたら予約を再取得（enabledがtrueの場合のみ）
  useEffect(() => {
    if (enabled) {
      fetchReservations();
    }
  }, [startDate, endDate, enabled]); // enabledも監視

  return {
    reservations,
    loading,
    error,
    fetchReservations,
    createReservation,
    updateReservation,
    deleteReservation,
    checkConflict,
    getReservationsForDate,
    getReservationsForRoomAndDate,
  };
};

export default useReservations;
